import { Fragment } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { usePromotionAudit } from '../../hooks';
import { conditionsToRoot } from '../../lib/conditionTree';
import type { ConditionTreeNode } from '../../lib/conditionTree';
import { buildPromotionSummary } from '../../lib/promoSummary';
import type { PromotionResponse } from '../../model/types';

export interface PromotionReviewDrawerProps {
  open: boolean;
  promotion: PromotionResponse | null;
  canApprove: boolean;
  approving: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

function renderConditionNode(node: ConditionTreeNode, t: (key: string, opts?: Record<string, unknown>) => string, depth = 0) {
  if (node.kind === 'leaf') {
    return (
      <ListItem key={node.uiId} sx={{ pl: 2 + depth * 2 }} disableGutters>
        <ListItemText
          primaryTypographyProps={{ variant: 'body2' }}
          primary={t(`conditionEditor.leafType.${node.type}`)}
          secondary={JSON.stringify(node.params)}
          secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'JetBrains Mono, monospace' } }}
        />
      </ListItem>
    );
  }
  return (
    <Fragment key={node.uiId}>
      <ListItem sx={{ pl: 2 + depth * 2 }} disableGutters>
        <ListItemText
          primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600 } }}
          primary={`${node.not ? t('conditionEditor.notLabel') + ' ' : ''}${t(`conditionEditor.combinatorToggle.${node.combinator}`)}`}
        />
      </ListItem>
      {node.children.map((child) => renderConditionNode(child, t, depth + 1))}
    </Fragment>
  );
}

/** Diff value shape is unspecified beyond `additionalProperties: true` — render defensively. */
function renderDiffValue(value: unknown): string {
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('old' in obj || 'new' in obj) return `${JSON.stringify(obj.old ?? obj.from)} → ${JSON.stringify(obj.new ?? obj.to)}`;
  }
  return JSON.stringify(value);
}

/** "Review" — read-only review drawer with a diff, for the approval queue (plan-011 §8.7). */
export function PromotionReviewDrawer({
  open,
  promotion,
  canApprove,
  approving,
  onClose,
  onApprove,
  onReject,
}: PromotionReviewDrawerProps) {
  const { t } = useTranslation('promotions');
  const audit = usePromotionAudit(promotion?.id, { size: 5 }, { enabled: open && Boolean(promotion) });
  const latestPendingEntry = audit.data?.content.find((e) => e.toStatus === 'PENDING_APPROVAL');

  if (!promotion) return null;

  const root = conditionsToRoot(promotion.conditions);
  const summary = buildPromotionSummary(t, promotion);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 560 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {promotion.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {promotion.code}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('reviewDrawer.summaryTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          </Box>

          {promotion.approvalReason ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('reviewDrawer.reasonTitle')}
              </Typography>
              <Chip size="small" color="warning" label={promotion.approvalReason} />
            </Box>
          ) : null}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('reviewDrawer.conditionsTitle')}
            </Typography>
            {root.children.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('conditionEditor.emptyHint')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {root.children.map((child) => renderConditionNode(child, t))}
              </List>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('reviewDrawer.actionsTitle')}
            </Typography>
            <List dense disablePadding>
              {promotion.actions.map((action, index) => (
                // Actions carry no stable id — index is a safe key here.
                <ListItem key={index} disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2' }}
                    primary={t(`rewardType.${action.type}`)}
                    secondary={JSON.stringify(action.params)}
                    secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'JetBrains Mono, monospace' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {latestPendingEntry?.diff ? (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('reviewDrawer.whatChangedTitle')}
                </Typography>
                <Stack spacing={0.5}>
                  {Object.entries(latestPendingEntry.diff).map(([key, value]) => (
                    <Typography key={key} variant="body2">
                      <strong>{key}:</strong> {renderDiffValue(value)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </>
          ) : null}
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Tooltip title={!canApprove ? t('approvals.approvePermissionHint') : ''}>
          <span>
            <Button onClick={onReject} color="error" disabled={!canApprove || approving}>
              {t('approvals.reject')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={!canApprove ? t('approvals.approvePermissionHint') : ''}>
          <span>
            <Button
              variant="contained"
              color="success"
              disabled={!canApprove || approving}
              startIcon={approving ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={onApprove}
            >
              {t('approvals.approve')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Drawer>
  );
}
